// src/pages/Home.js 
import React, { useState, useEffect } from 'react';
import {ChevronDown } from 'lucide-react';
import backgroundVideo from '../assets/home_background.mp4';

import aboutImage1 from '../assets/about1.png';
import aboutImage2 from '../assets/about2.webp';
import algaeImage1 from '../assets/algae1.webp';
import algaeImage2 from '../assets/algae2.webp';
import ourProduct1 from '../assets/ourProduct1.webp';
import ourProduct2 from '../assets/ourProduct2.webp';
import ourProduct3 from '../assets/ourProduct3.webp';
import ourProduct4 from '../assets/ourProduct4.webp';

const SectionWithImages = ({ id, title, text, images, reverse = false, labels = [] }) => (
  <div
    id={id}
    className={`flex flex-col ${reverse ? 'sm:flex-row-reverse' : 'sm:flex-row'} items-center text-white bg-gray-900 p-12 sm:p-20 gap-8`}
  >
    <div className="w-full sm:w-1/2 text-center sm:text-left">
      <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4">{title}</h2>
      <p className="text-lg sm:text-xl animate-fadeIn animate-delay-300 text-justify">{text}</p>
    </div>

    <div className={`w-full sm:w-1/2 grid grid-cols-1 ${images.length > 1 ? 'sm:grid-cols-2' : ''} gap-4`}>
      {images.map((img, index) => (
        <div key={index} className="flex flex-col items-center">
          <img
            src={img}
            alt={`${title} ${index + 1}`}
            className="rounded-lg shadow-lg transform hover:scale-105 transition duration-300 w-full h-64 sm:h-80 object-cover"
          />
          {labels[index] && (
            <p className="mt-2 text-sm sm:text-base text-gray-300">{labels[index]}</p>
          )}
        </div>
      ))}
    </div>
  </div>
);

const Home = () => {
  const [showScrollIndicator, setShowScrollIndicator] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setShowScrollIndicator(false), 5000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="w-full m-0 p-0 overflow-x-hidden">
      {/* Hero Section with Background Video */}
      <div className="relative h-screen w-full overflow-hidden">
        <video
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          poster="../assets/Video_thumbnail.png"
          className="absolute inset-0 w-full h-full object-cover brightness-75 bg-black"
        >
          <source src={backgroundVideo} type="video/mp4" />
          Your browser does not support the video tag.
        </video>

        {/* Scroll Indicator */}
        {showScrollIndicator && (
          <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 text-white animate-bounce">
            <ChevronDown size={40} />
          </div>
        )}

        {/* Content Overlay */}
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center text-white px-6 sm:px-12">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4 animate-fadeIn">
            Leveraging Microalgae's Potential for Sustainable Development
          </h1>
          <p className="text-lg sm:text-xl max-w-3xl animate-fadeIn animate-delay-300">
            Proalgenix specializes in harnessing microalgae-based solutions for applications in food,
            animal feed, nutritional supplements, agriculture, and bioenergy.
          </p>
        </div>
      </div>


      {/* About Us Section */}
      <SectionWithImages
        id="about"
        title="About Us"
        text="Proalgenix aims to fully capitalize on the advantages of microalgae for use in food, feed, nutraceuticals, agriculture, cosmetics, and other fields. We are launching a state-of-the-art facility in India to drive innovation and production of microalgae-based solutions. This is just the beginning of our journey!"
        images={[aboutImage1, aboutImage2]}
        reverse={false}
      />

      {/* Microalgae Section */}
      <SectionWithImages
        id="microalgae"
        title="Microalgae"
        text="Microalgae, a physiologically diverse group of microorganisms, are capable of synthesizing a wide array of bioactive compounds. They serve as a versatile platform for photosynthetic bio-manufacturing, characterized by their productivity, scalability, sustainability, programmability, and edibility."
        images={[algaeImage1, algaeImage2]}
        reverse={true}
      />

      {/* Our Products Section */}
      <div
        id="our-products" className="flex flex-col items-center text-white bg-gray-900 p-12 sm:p-20 gap-8"
      >
        <div className="w-full text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4">Our Products</h2>
          <p className="text-lg sm:text-xl animate-fadeIn animate-delay-300 text-justify max-w-5xl mx-auto">
            With an eye on dietary supplements, food and beverage, feed, and agro applications, our products would be made to demonstrate microalgae solutions with the highest quality standards to the most competitive markets.
          </p>
        </div>

        <div className="w-full max-w-6xl grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 mt-10">
          {[ourProduct1, ourProduct2, ourProduct3, ourProduct4].map((img, index) => (
            <div key={index} className="flex flex-col items-center">
              <img
                src={img}
                alt={`Our Product ${index + 1}`}
                className="rounded-lg shadow-lg w-full h-64 object-cover transition-transform duration-300 hover:scale-105"
              />
              <p className="mt-3 text-sm sm:text-base text-gray-300 text-center">
                {['Human Health', 'Animal feed', 'Agriculture', 'Bioenergy and Biofuel'][index]}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Home;
